import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// v5.8.36 (Q1 follow-up) — Detect the env-file-missing misconfiguration
// at module init.  If REACT_APP_BACKEND_URL was not baked into the build,
// every API call resolves to `undefined/api/...` which the browser sends
// to the frontend host itself, gets a 404, and the AuthContext catch
// shows a generic "Login failed" toast — no clue to the user that the
// actual problem is a missing env var.  Expose a global so the
// AuthContext can surface a precise error message.
export const API_MISCONFIGURED = !BACKEND_URL;
if (API_MISCONFIGURED && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[fws] REACT_APP_BACKEND_URL is not set.  ' +
      'Copy frontend/.env.example to frontend/.env and restart the dev server.  ' +
      'All API calls will fail until this is fixed.'
  );
}
export const API_BASE = `${BACKEND_URL}/api`;

// 2026-07-23 (owner decision) — % owned is scoped to the SELECTED league
// (finalized teams only). App.js keeps this in sync with the league picker;
// a request interceptor appends it to every GET /entities* call so every
// browse/detail surface shows league-scoped ownership without per-page
// plumbing. Unknown query params are ignored by non-enriched endpoints.
let _entityLeagueScope = '';
export const setEntityLeagueScope = (leagueId) => { _entityLeagueScope = leagueId || ''; };

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // SECURITY: send HttpOnly auth cookie with every request
  // 2026-07 UX audit P0 — without a timeout a silently-dropped connection
  // (Cloudflare 52x class) leaves spinners pending forever. 20s covers slow
  // mobile networks; long uploads override per-request.
  timeout: 20000,
});

// 2026-07-23 — league-scoped % owned on entity browse/detail GETs (see
// setEntityLeagueScope above). Runs before the request leaves the client.
api.interceptors.request.use((config) => {
  if (_entityLeagueScope &&
      (config.method || 'get').toLowerCase() === 'get' &&
      (config.url || '').startsWith('/entities') &&
      !(config.params && config.params.league_id)) {
    config.params = { ...(config.params || {}), league_id: _entityLeagueScope };
  }
  return config;
});

// SECURITY (v5.7.35): Removed the previous request interceptor that
// read `fantasy_token` from localStorage and sent it as
// `Authorization: Bearer …`. That code totally undermined the HttpOnly
// cookie defence — any XSS could read the token and re-attach it,
// bypassing HttpOnly. Auth is now strictly cookie-only.
//
// On first load (v5.7.35 upgrade) we proactively purge any stale
// localStorage entries from the old auth scheme so they can't be
// scraped later. This is idempotent and runs once per page load.
if (typeof window !== 'undefined' && window.localStorage) {
  try {
    window.localStorage.removeItem('fantasy_token');
    window.localStorage.removeItem('fantasy_user');
  } catch {
    // localStorage can be disabled (Safari private mode); ignore.
  }
}

// Handle 401s — redirect to home only when an authenticated action fails
// from inside the app. Two important exceptions:
//   1. The initial `/auth/me` probe is allowed to 401 (anonymous browsing).
//   2. Public routes (`/credits`, `/people`, `/legal/*`) stay
//      accessible without auth, so a 401 from any background call on those
//      pages must NOT bounce the user back to `/`.
//
// v5.7.79 — Refresh-token rotation interceptor.
// Before deciding to redirect on 401, we attempt ONE silent refresh by
// calling `POST /api/auth/refresh`. The backend reads the HttpOnly
// `fantasy_refresh` cookie (scoped to `/api/auth/`), rotates both
// access + refresh, sets new cookies, and returns 200. We then retry
// the original request once. If the refresh ALSO 401s, we fall back
// to the legacy redirect.
//
// Request queue: while a refresh is in flight, any other 401 from a
// parallel request is queued behind the same refresh promise so we
// only fire ONE rotation call per refresh-eligible 401 burst. This is
// the classic "axios refresh queue" pattern; without it, a page that
// fires 5 parallel APIs on mount would trigger 5 refresh calls, the
// last 4 of which would all detect "replay" and revoke the family.
// AUDIT #11 (2026-06) — `/reset-password`, the OAuth callback prefix
// (`/auth/oauth/`), and the public `/privacy` + `/terms` pages were missing,
// so a background 401 fired while on those pages hard-redirected to `/`,
// breaking the password-reset / OAuth-completion flows mid-stream.
const PUBLIC_ROUTES = [
  '/credits', '/people', '/legal/',
  '/reset-password', '/auth/oauth/', '/privacy', '/terms',
];

// 2026-07 (pod-rollover fix) — transient retry for the credential-granting
// auth calls. During a Kubernetes pod replacement the backend is briefly
// unreachable (~2s) and Cloudflare returns 502/503/504/52x (or the browser
// sees a network error). Those errors mean the request NEVER reached the
// app, so retrying login/register/guest is safe. 2 retries with 1.5s/3s
// backoff cover the observed dead-air window. Timeouts/cancels are
// ambiguous (request may have been processed) and are NOT retried.
const AUTH_RETRY_PATHS = ['/auth/login', '/auth/register', '/auth/guest'];
const AUTH_TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const AUTH_RETRY_MAX = 2;
const AUTH_RETRY_BACKOFF_MS = 1500;
const _isAuthCredentialCall = (config) =>
  AUTH_RETRY_PATHS.some((p) => (config?.url || '').includes(p));

let _refreshPromise = null;
const _shouldAttemptRefresh = (config) => {
  if (!config) return false;
  const url = config.url || '';
  // Don't refresh on the auth probes themselves (would infinite-loop).
  if (url.endsWith('/auth/me')) return false;
  if (url.endsWith('/auth/refresh')) return false;
  if (url.endsWith('/auth/login')) return false;
  if (url.endsWith('/auth/register')) return false;
  if (url.endsWith('/auth/guest')) return false;
  // 2026-08-12 audit — an OAuth login/verify POST 401s when the provider token
  // is bad/expired. The caller is anonymous (no session), so a silent refresh
  // is pointless AND, worse, on failure it falls through to the hard-redirect
  // below and bounces the user off the /auth page instead of showing an inline
  // "sign-in failed" error. Never refresh-retry an oauth POST.
  if (url.includes('/auth/oauth/')) return false;
  // Don't retry a request we've already retried once.
  if (config._refreshRetried) return false;
  return true;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 2026-07 — auth-call transient retry (see AUTH_RETRY_PATHS above).
    const _authCfg = error.config;
    if (_authCfg && _isAuthCredentialCall(_authCfg)) {
      const _status = error.response?.status;
      const _transient =
        (typeof _status === 'number' && AUTH_TRANSIENT_STATUSES.has(_status)) ||
        (!error.response && error.code === 'ERR_NETWORK');
      const _attempt = _authCfg._authRetryCount || 0;
      if (_transient && _attempt < AUTH_RETRY_MAX) {
        _authCfg._authRetryCount = _attempt + 1;
        await new Promise((r) => setTimeout(r, AUTH_RETRY_BACKOFF_MS * (_attempt + 1)));
        return api.request(_authCfg);
      }
    }
    // v6.1.17.7 N.2 — Username gate.  When the backend's
    // `RequireUsernameSetMiddleware` returns 403 USERNAME_NOT_SET we
    // dispatch a global event the UsernamePickerModal listens on.
    // The original request rejects; the modal re-issues it after a
    // successful PATCH /users/me/username (or the caller retries).
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail?.code === 'USERNAME_NOT_SET' &&
      typeof window !== 'undefined'
    ) {
      try {
        window.dispatchEvent(new CustomEvent('fws:username-required', {
          detail: { reason: error.response.data.detail },
        }));
      } catch { /* ignore */ }
    }
    // 18+ gate (2026-06).  When the backend's RequireAgeConfirmedMiddleware
    // returns 403 AGE_CONFIRMATION_REQUIRED we dispatch a global event the
    // AgeGateHost (app root) listens on. The original request rejects; the
    // host re-issues it (via reload) after POST /api/auth/confirm-age.
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail?.code === 'AGE_CONFIRMATION_REQUIRED' &&
      typeof window !== 'undefined'
    ) {
      try {
        window.dispatchEvent(new CustomEvent('fws:age-required', {
          detail: { reason: error.response.data.detail },
        }));
      } catch { /* ignore */ }
    }
    if (error.response?.status === 401 && _shouldAttemptRefresh(error.config)) {
      try {
        if (!_refreshPromise) {
          _refreshPromise = api.post('/auth/refresh').finally(() => {
            _refreshPromise = null;
          });
        }
        await _refreshPromise;
        // Refresh succeeded — retry the original request once.
        error.config._refreshRetried = true;
        return api.request(error.config);
      } catch (refreshErr) {
        // Refresh itself 401'd → fall through to the redirect below.
      }
    }
    if (error.response?.status === 401) {
      // v6.1.5 Phase G5 A.2 — Stale-flag cleanup.  Any 401 (whether the
      // refresh-retry above succeeded or not) means at LEAST one of our
      // tokens is no longer valid.  Clear the per-tab session-active
      // flag so the next reload sees the splash again — matching the
      // user's mental model of "I got logged out".  This is purely
      // additional cleanup; the existing redirect logic below is
      // unchanged.
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem('fws_session_active');
        }
      } catch { /* ignore */ }

      const url = error.config?.url || '';
      // Don't redirect when the 401 came from the auth-probe endpoints
      // themselves — these are best-effort calls; anonymous visitors
      // 401 here every cold load and that's expected.
      const isAuthProbe =
        url.endsWith('/auth/me') ||
        url.endsWith('/auth/refresh') ||
        // 2026-08-12 audit — a failed OAuth login/verify must surface inline on
        // the /auth page, not hard-redirect the user to the landing page.
        url.includes('/auth/oauth/');
      const path = window.location.pathname;
      const onPublicRoute = PUBLIC_ROUTES.some((p) => path === p || path.startsWith(p));
      if (!isAuthProbe && !onPublicRoute && path !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
// v5.7.52 — register + guest accept an optional `turnstileToken` 2nd arg
// that gets attached as an `X-Turnstile-Token` header. When the frontend
// has no site key configured (REACT_APP_TURNSTILE_SITE_KEY unset) the
// token is null and the backend's turnstile check is also disabled, so
// this is a graceful no-op for dev.
// 18+ gate (2026-08 compliance audit) — read the persisted AgeGateModal
// answer so account-creation calls carry a REAL attestation to the server.
const _ageAttested = () => {
  try { return localStorage.getItem('fws_age_confirmed') === 'true'; } catch { return false; }
};

export const authAPI = {
  register: (data, turnstileToken) => api.post(
    '/auth/register',
    data,
    turnstileToken ? { headers: { 'X-Turnstile-Token': turnstileToken } } : undefined,
  ),
  login: (data) => api.post('/auth/login', data),
  // 2026-08-11 — social login (Google Identity Services / Apple JS popup).
  // The backend (routes/identity.py) expects {id_token}; a response may be a
  // session, or {link_required, confirmation_token} for an existing email.
  oauthConfigured: () => api.get('/auth/oauth/configured'),
  oauthLogin: (provider, idToken) => api.post(`/auth/oauth/${provider}`, { id_token: idToken, age_confirmed: _ageAttested() }),
  oauthLinkConfirm: (payload) => api.post('/auth/oauth/link/confirm', payload),
  // 2026-09-17 — password-less link for provider-hosted mailboxes (gmail via
  // Google, icloud via Apple). Disables the old password server-side.
  oauthLinkVerified: (confirmationToken) => api.post('/auth/oauth/link/verified', { confirmation_token: confirmationToken }),
  // 2026-09-17 — authenticated email change (password re-confirmed).
  changeEmail: (newEmail, currentPassword) => api.post('/auth/change-email', { new_email: newEmail, current_password: currentPassword }),
  guest: (turnstileToken) => api.post(
    '/auth/guest',
    { age_confirmed: _ageAttested() },
    turnstileToken ? { headers: { 'X-Turnstile-Token': turnstileToken } } : undefined,
  ),
  me: () => api.get('/auth/me'),
  // 18+ gate (2026-06) — persist the 18+ attestation server-side. Called by
  // the AgeGateModal / AgeGateHost after the user taps "Yes, I'm 18+".
  confirmAge: () => api.post('/auth/confirm-age'),
  logout: () => api.post('/auth/logout'),  // SECURITY: Clear HttpOnly cookie
  logoutAll: () => api.post('/auth/logout-all'),
  // v5.7.79 — manual refresh (also fires automatically via the 401 interceptor)
  refresh: () => api.post('/auth/refresh'),
  // v5.7.80 — MFA challenge step in the login flow
  mfaVerify: ({ mfa_challenge_token, code, backup_code }) =>
    api.post('/auth/mfa/verify', { mfa_challenge_token, code, backup_code }),
  // v6.x — Forgot-password flow (unauthenticated). Request always returns
  // a generic 200 (email-enumeration-safe); confirm completes via the
  // token from the emailed /reset-password?token=… link.
  passwordResetRequest: (email) => api.post('/auth/password-reset/request', { email }),
  passwordResetConfirm: (reset_token, new_password) =>
    api.post('/auth/password-reset/confirm', { reset_token, new_password }),
  // v6.x — Self-service account deletion (Apple 5.1.1(v) / Google Play).
  // Anonymizes the account; password accounts must pass current_password,
  // guest/OAuth-only accounts pass confirm:true. Backend kills all sessions.
  deleteAccount: ({ current_password, confirm }) =>
    api.post('/auth/delete-account', { current_password, confirm }),
};

// Entities
export const entityAPI = {
  list: (params) => api.get('/entities', { params }),
  picker: () => api.get('/entities/picker'),
  get: (id) => api.get(`/entities/${id}`),
  categories: () => api.get('/entities/categories/stats'),
  starterPicks: () => api.get('/entities/starter-picks'),
  topMovers: (params) => api.get('/entities/top-movers', { params }),
};

// Teams
export const teamAPI = {
  // Phase D: myTeams accepts optional {envelope} to opt into v2 response
  // shape {teams, still_onboarding, attached, total}. Default (no arg)
  // keeps the legacy {teams: [...]} shape.
  myTeams: ({ envelope } = {}) =>
    api.get(envelope === 'v2' ? '/teams/my?envelope=v2' : '/teams/my'),
  get: (id) => api.get(`/teams/${id}`),
  // 2026-09-03 — drop dialog hint: refund, cash after, next-move fee,
  // cheapest refill for the slot type, and whether the strand guard blocks.
  dropPreview: (teamId, entityId) => api.get(`/teams/${teamId}/drop-preview`, { params: { entity_id: entityId } }),
  // 2026-08-27 — successful drafts broadcast an event so the one-time
  // guest "save your team" prompt can trigger after the FIRST pick.
  draft: async (data) => {
    const res = await api.post('/teams/draft', data);
    try {
      window.dispatchEvent(new CustomEvent('fws:draft-success', { detail: res?.data || {} }));
      // 2026-08-28 (iter186 HIGH fix) — refresh the App-level team snapshot so
      // Stage never renders a stale roster after drafting from a sub-view.
      window.dispatchEvent(new CustomEvent('fws:team-refresh'));
    } catch (e) { /* non-browser env */ }
    return res;
  },
  setCaptain: (data) => api.post('/teams/captain', data),
  // 2026-08-31 — one-tap smart-random roster fill; same events as draft so
  // the finalize prompt + App team snapshot react automatically.
  draftRandom: async (data) => {
    try {
      const res = await api.post('/teams/draft-random', data);
      try {
        window.dispatchEvent(new CustomEvent('fws:draft-success', { detail: res?.data || {} }));
      } catch (e) { /* non-browser env */ }
      return res;
    } finally {
      // 2026-06 (draft-random desync fix) — refresh the App team snapshot on
      // BOTH success and failure. A timed-out request may still have landed
      // server-side; refetching keeps roster count / Finalize state in sync.
      try {
        window.dispatchEvent(new CustomEvent('fws:team-refresh'));
      } catch (e) { /* non-browser env */ }
    }
  },
  transfer: (data) => api.post('/teams/transfer', data),
  remove: (data) => api.post('/teams/remove', data),
  finalize: async (teamId) => {
    const res = await api.post('/teams/finalize', null, { params: { team_id: teamId } });
    // 2026-08-28 — broadcast so the guest "save your team" prompt can fire
    // at the peak "I'm competing" moment; refresh the App team snapshot too.
    try {
      window.dispatchEvent(new CustomEvent('fws:finalize-success', { detail: res?.data || {} }));
      window.dispatchEvent(new CustomEvent('fws:team-refresh'));
    } catch (e) { /* non-browser env */ }
    return res;
  },
  // v6.1.17.7 N.2 Team-UI — Inline rename target.  Backend gates with
  // 1/week Redis SETNX + profanity filter + owner check + admin exempt.
  // See `backend/routes/identity.py::patch_team_name`.
  rename: (leagueId, teamId, name) =>
    api.patch(`/leagues/${leagueId}/teams/${teamId}/name`, { name }),
  // 2026-07-21 (owner request) — weekly worth series for the Stage
  // Portfolio sparkline + the rival "worth over time" plot.
  valueHistory: (teamId) => api.get(`/teams/${teamId}/value-history`),
};

// Leagues
export const leagueAPI = {
  list: () => api.get('/leagues'),
  get: (id) => api.get(`/leagues/${id}`),
  create: (data) => api.post('/leagues', data),
  join: (data) => api.post('/leagues/join', data),
  update: (id, data) => api.put(`/leagues/${id}`, data),
  pool: (id) => api.get(`/leagues/${id}/pool`),
  kick: (leagueId, userId) => api.post(`/leagues/${leagueId}/kick/${userId}`),
  discover: (params) => api.get('/leagues/discover', { params }),
  deadline: (id) => api.get(`/leagues/${id}/deadline`),
  // 2026-09-03 — per-league roster-move feed (Stage "Transfer Activity").
  transferActivity: (id, params) => api.get(`/leagues/${id}/transfer-activity`, { params }),
};

// 2026-09-03 — per-user in-app notices (e.g. the drop-fee refund card).
export const noticesAPI = {
  list: () => api.get('/notices'),
  dismiss: (id) => api.post(`/notices/${id}/dismiss`),
};

// Leaderboard
export const leaderboardAPI = {
  get: (leagueId, params) => api.get(`/leaderboard/${leagueId}`, { params }),
  global: (params) => api.get('/leaderboard/global/top', { params }),
  // v5.7.63 — Locked self-row at the bottom of Rankings for huge leagues.
  myRank: (leagueId) => api.get(`/leaderboard/${leagueId}/my-rank`),
};

// News
export const newsAPI = {
  list: (params) => api.get('/news', { params }),
  // v5.7.89 — paginated My-Team news (was previously read inline by NewsTab
  // without an exported helper, which caused the Load-More button to fall
  // back to the World endpoint).
  my: (params) => api.get('/news/my', { params }),
};

// v5.7.89 — League cap info for Create/Join screens
export const leaguesAPI = {
  capInfo: () => api.get('/leagues/cap-info'),
};

// Scoring
export const scoringAPI = {
  rules: () => api.get('/scoring/rules'),
};

// Seed
export const seedAPI = {
  all: () => api.post('/seed/all'),
};

// Admin
export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  // Portrait Gallery
  listPortraits: (params) => api.get('/admin/portraits', { params }),
  portraitStats: () => api.get('/admin/portraits/stats'),
  flagPortrait: (entityId, reason) => api.post(`/admin/portraits/${entityId}/flag`, { reason }),
  unflagPortrait: (entityId) => api.post(`/admin/portraits/${entityId}/unflag`),
  flaggedNames: () => api.get('/admin/portraits/flagged/names'),
  // 3-portrait system (2026-06)
  getEntityPortraits: (entityId) => api.get(`/admin/entities/${entityId}/portraits`),
  selectPortrait: (entityId, variant) => api.post(`/admin/entities/${entityId}/portraits/select`, { variant }),
  generatePortraits: (entityId, variants, setActive = true) =>
    api.post(`/admin/entities/${entityId}/portraits/generate`, { variants, set_active: setActive }),
  applyAllPortraits: (variant, onlyActive = true) =>
    api.post('/admin/portraits/apply-all', { variant, only_active: onlyActive }),
  // Security audit
  runSecurityAudit: () => api.post('/admin/security-audit/run'),
  listSecurityReports: () => api.get('/admin/security-audit/reports'),
  readSecurityReport: (filename) => api.get(`/admin/security-audit/reports/${encodeURIComponent(filename)}`),
  retrySecurityAuditSlot: (filename, provider) => api.post('/admin/security-audit/retry-slot', { report_filename: filename, provider }),
  getJob: (jobId) => api.get(`/admin/jobs/${jobId}`),
};
